import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { Pagination } from "@/components/Pagination";
import { Plus, Search, Filter, Package, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { format, isBefore, isWithinInterval, addDays } from "date-fns";

const PAGE_SIZE = 25;

export default function OrdersListPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [karigars, setKarigars] = useState([]);
  const [filters, setFilters] = useState({ status: "all", priority: "all", karigar_id: "all", search: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.priority && filters.priority !== "all") params.priority = filters.priority;
      if (filters.karigar_id && filters.karigar_id !== "all") params.karigar_id = filters.karigar_id;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get("/orders", { params });
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters, page]);
  useEffect(() => { setPage(1); }, [filters.status, filters.priority, filters.karigar_id, filters.search]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/users", { params: { role: "karigar" } }); setKarigars(data); } catch (err) { void err; }
  })(); }, []);

  const canCreate = can(user, "orders.create");
  const canSeeClient = can(user, "orders.see_client");
  const isClient = user?.role === "client";

  const nearestDelivery = (o) => {
    const dates = (o.items || []).map(i => i.delivery_date).filter(Boolean).map(d => new Date(d));
    if (!dates.length) return null;
    return new Date(Math.min(...dates.map(d => d.getTime())));
  };

  return (
    <div className="space-y-6" data-testid="orders-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">
            {isClient ? "TARU" : "Manufacturing"}
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">
            {isClient ? "My Orders" : "Orders"}
          </h1>
          {isClient && (
            <p className="text-sm text-[#4A5A50] mt-1">Tap any order to see live tracking.</p>
          )}
        </div>
        {canCreate && (
          <Button onClick={() => nav("/orders/new")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-order-btn">
            <Plus size={16} className="mr-1"/> New Order
          </Button>
        )}
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4">
          <div className={`grid grid-cols-1 gap-3 ${isClient ? "md:grid-cols-2" : "md:grid-cols-4"}`}>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
              <Input
                placeholder={isClient ? "Search order reference" : "Search reference, client…"}
                className="pl-9 rounded-lg"
                value={filters.search}
                onChange={(e)=>setFilters({...filters, search:e.target.value})}
                data-testid="orders-search"
              />
            </div>
            <Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status:v})}>
              <SelectTrigger className="rounded-lg" data-testid="filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["new","accepted","in-progress","completed","on-hold"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={(v)=>setFilters({...filters, priority:v})}>
              <SelectTrigger className={`rounded-lg ${isClient ? "hidden" : ""}`} data-testid="filter-priority"><SelectValue placeholder="Priority"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {["urgent","fast","normal"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.karigar_id} onValueChange={(v)=>setFilters({...filters, karigar_id:v})}>
              <SelectTrigger className={`rounded-lg ${isClient ? "hidden" : ""}`} data-testid="filter-karigar"><SelectValue placeholder="Karigar"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All karigars</SelectItem>
                {karigars.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center text-[#4A5A50] py-12" data-testid="orders-loading">Loading orders…</div>
      ) : orders.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="py-16 text-center">
            <Package size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
            <div className="font-serif text-xl">No orders match these filters</div>
            <div className="text-[#4A5A50] text-sm mt-1">Try clearing filters or create your first order.</div>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map(o => {
            const nd = nearestDelivery(o);
            const overdue = nd && isBefore(nd, new Date()) && o.status !== "completed";
            const dueSoon = nd && !overdue && isWithinInterval(nd, { start: new Date(), end: addDays(new Date(), 7) });
            return (
              <button
                key={o.id}
                onClick={() => nav(`/orders/${o.id}`)}
                className="text-left bg-white rounded-xl border border-[#E4DFCF] p-5 card-hover"
                data-testid={`order-card-${o.reference_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-serif text-xl font-medium">{o.reference_number}</div>
                    {canSeeClient && <div className="text-sm text-[#4A5A50] mt-0.5">{o.client_name || "No client"}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusPill status={o.priority}/>
                    <StatusPill status={o.status}/>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <Package size={14} className="text-[#4A5A50]"/><span>{(o.items||[]).length} item{(o.items||[]).length===1?"":"s"}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <Calendar size={14} className={overdue?"text-red-600":dueSoon?"text-amber-600":"text-[#4A5A50]"}/>
                  <span className={overdue?"text-red-600 font-medium":dueSoon?"text-amber-600 font-medium":"text-[#4A5A50]"}>
                    {nd ? format(nd, "dd MMM yyyy") : "No delivery date"}
                    {overdue ? " · Overdue" : dueSoon ? " · Due soon" : ""}
                  </span>
                </div>
                <div className={`mt-3 pt-3 border-t border-[#E4DFCF] text-xs text-[#4A5A50] ${isClient ? "hidden" : ""}`}>
                  Karigar: <span className="text-[#0F1E15]">{o.karigar_name || "Unassigned"}</span>
                </div>
                {isClient && (
                  <div className="mt-3 pt-3 border-t border-[#E4DFCF] text-xs text-[#0F5E39] font-medium">
                    Tap to see live tracking →
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage}/>
        </>
      )}
    </div>
  );
}
